import {
  bonusDisplayStatus,
  bonusDisplayStatusTone,
  bonusListSubtitle,
  bonusProgressAmountsLabel,
  bonusProgressPercent,
  formatBonusAmount,
  formatBonusDateRange,
  isRewardBonusType,
  type BonusTransaction,
} from '@/domain/bonuses';
import { cn } from '@/lib/cn';
import { Badge } from '../common/badge';
import { Icon } from '../common/icon';
import { BonusProgress } from './BonusProgress';

export interface BonusListRowProps {
  bonus: BonusTransaction;
  currency?: string;
  onSelect?: (bonus: BonusTransaction) => void;
  className?: string;
}

export function BonusListRow({
  bonus,
  currency = '',
  onSelect,
  className,
}: BonusListRowProps) {
  const statusLabel = bonusDisplayStatus(bonus);
  const showProgress = !isRewardBonusType(bonus.Type);
  const dateRange = formatBonusDateRange(bonus.StartDate, bonus.ExpiryDate);
  const percent = bonusProgressPercent(bonus);
  const amounts = bonusProgressAmountsLabel(bonus, currency);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(bonus)}
      className={cn(
        'flex w-full flex-col gap-3 rounded-md border border-border-default bg-bg-surface',
        'px-4 py-3.5 text-left transition-colors hover:bg-bg-elevated',
        'focus-visible:ring-1 focus-visible:ring-border-focus focus-visible:outline-none',
        className,
      )}
    >
      <div className="flex w-full items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="truncate text-[14px] font-bold text-text-primary">
            {bonus.Name || 'Bonus'}
          </p>
          <p className="truncate text-[12px] text-text-secondary">
            {bonusListSubtitle(bonus)}
          </p>
          {dateRange ? (
            <p className="truncate text-[11px] text-[#b8afa3]">{dateRange}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={bonusDisplayStatusTone(statusLabel)} size="sm">
            {statusLabel}
          </Badge>
          <p className="text-[14px] font-bold text-text-primary">
            {formatBonusAmount(bonus.Amount, currency)}
          </p>
        </div>
        <Icon name="arrow-right" size={16} className="shrink-0 text-icon-muted" />
      </div>
      {showProgress ? (
        <BonusProgress amounts={amounts} percent={percent} size="compact" />
      ) : null}
    </button>
  );
}
